import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  message,
  Spin,
  Tag,
  Progress,
  Divider,
  Form,
  Input,
  Switch,
  InputNumber,
  Space,
  Typography,
  Alert,
  Upload,
  Image,
  Modal,
  Tabs
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  CodeOutlined,
  DockerOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  GlobalOutlined,
  MailOutlined,
  CreditCardOutlined,
  ApiOutlined,
  CloudUploadOutlined,
  MonitorOutlined
} from '@ant-design/icons';
import ImageCropModal from '../../components/ImageCrop/ImageCropModal';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface SystemInfo {
  nodejs: {
    version: string;
    status: 'ok' | 'error';
  };
  mysql: {
    version: string;
    status: 'ok' | 'error';
    connection: boolean;
  };
  docker: {
    version: string;
    status: 'ok' | 'error';
    running: boolean;
  };
  system: {
    platform: string;
    arch: string;
    uptime: number;
    memory: {
      total: number;
      used: number;
      free: number;
    };
    disk: {
      total: number;
      used: number;
      free: number;
    };
  };
}

interface SystemConfig {
  site_name: string;
  site_description: string;
  maintenance_mode: boolean;
  max_servers_per_user: number;
  debug_mode: boolean;
  auto_backup: boolean;
  backup_interval: number;
  email_enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  email_sender_name: string;
  panel_todo_admin_email: string;
  steam_api_key: string;
  logo_url: string;
  logo_width: number;
  logo_height: number;
  taobao_verification_enabled: boolean;
  // 彩虹易支付配置
  rainbow_pay_enabled: boolean;
  rainbow_pay_pid: string;
  rainbow_pay_key: string;
  rainbow_pay_api_url: string;
  rainbow_pay_notify_url: string;
  rainbow_pay_return_url: string;
  // 支付方式开关
  rainbow_pay_wechat_enabled: boolean;
  rainbow_pay_alipay_enabled: boolean;
  rainbow_pay_qq_enabled: boolean;
}

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [form] = Form.useForm();
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoModalVisible, setLogoModalVisible] = useState(false);
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string>('');
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);

  // 获取系统信息
  const fetchSystemInfo = async () => {
    setLoading(true);
    try {
      console.log('开始获取系统信息...');
      const response = await api.get('/admin/system/info');
      console.log('系统信息响应:', response.data);
      setSystemInfo(response.data);
    } catch (error: any) {
      console.error('获取系统信息失败:', error);
      console.error('错误详情:', error.response?.data || error.message);
      message.error(`获取系统信息失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 获取系统配置
  const fetchConfig = useCallback(async () => {
    try {
      console.log('开始获取系统配置...');
      const response = await api.get('/admin/system/config');
      console.log('系统配置响应:', response.data);
      setConfig(response.data);
      form.setFieldsValue(response.data);
    } catch (error: any) {
      console.error('获取系统配置失败:', error);
      console.error('错误详情:', error.response?.data || error.message);
      message.error(`获取系统配置失败: ${error.response?.data?.message || error.message}`);
    }
  }, [form]);

  // 保存系统配置
  const saveConfig = async (values: SystemConfig) => {
    try {
      await api.put('/admin/system/config', values);
      message.success('配置保存成功');
      setConfig(values);
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    }
  };

  // 测试连接
  const testConnection = async (type: string) => {
    try {
      await api.post(`/admin/system/test/${type}`);
      message.success(`${type} 连接测试成功`);
      fetchSystemInfo(); // 刷新系统信息
    } catch (error) {
      message.error(`${type} 连接测试失败`);
    }
  };

  // 处理文件选择
  const handleFileSelect = (file: File) => {
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      message.error('请选择图片文件');
      return false;
    }

    // 检查文件大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('文件大小不能超过5MB');
      return false;
    }

    // 保存文件并打开剪裁模态框
    setTempImageFile(file);
    const imageUrl = URL.createObjectURL(file);
    setCropImageUrl(imageUrl);
    setCropModalVisible(true);
    return false; // 阻止默认上传行为
  };

  // 处理剪裁完成
  const handleCropComplete = async (croppedImageUrl: string) => {
    if (!tempImageFile) return;

    try {
      // 将剪裁后的图片转换为File对象
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const croppedFile = new File([blob], tempImageFile.name, { type: tempImageFile.type });

      // 上传剪裁后的图片
      const formData = new FormData();
      formData.append('logo', croppedFile);

      const uploadResponse = await api.post('/admin/system/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      message.success('LOGO上传成功');
      setConfig(prev => prev ? { ...prev, logo_url: uploadResponse.data.logoUrl } : null);
      setLogoPreview(uploadResponse.data.logoUrl);
      fetchConfig(); // 刷新配置
      
      // 清理临时文件
      URL.revokeObjectURL(croppedImageUrl);
      setTempImageFile(null);
      setCropModalVisible(false);
    } catch (error: any) {
      message.error(`LOGO上传失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 取消剪裁
  const handleCropCancel = () => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropImageUrl('');
    setTempImageFile(null);
    setCropModalVisible(false);
  };

  // 删除LOGO
  const handleLogoDelete = async () => {
    try {
      await api.delete('/admin/system/logo');
      message.success('LOGO删除成功');
      setConfig(prev => prev ? { ...prev, logo_url: '' } : null);
      setLogoPreview('');
      fetchConfig(); // 刷新配置
    } catch (error: any) {
      message.error(`删除LOGO失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 预览LOGO
  const handleLogoPreview = () => {
    if (config?.logo_url) {
      setLogoPreview(config.logo_url);
      setLogoModalVisible(true);
    }
  };

  useEffect(() => {
    fetchSystemInfo();
    fetchConfig();
  }, [fetchConfig]);

  const getStatusTag = (status: string, running?: boolean) => {
    if (status === 'ok' && running !== false) {
      return <Tag color="green" icon={<CheckCircleOutlined />}>正常</Tag>;
    }
    return <Tag color="red" icon={<CloseCircleOutlined />}>异常</Tag>;
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}天 ${hours}小时 ${minutes}分钟`;
  };

  // 系统状态组件
  const SystemStatusCard = () => (
    <Card
      title={
        <Space>
          <MonitorOutlined />
          系统状态
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            onClick={fetchSystemInfo}
            loading={loading}
          >
            刷新
          </Button>
        </Space>
      }
    >
      {systemInfo ? (
        <Row gutter={[16, 16]}>
          {/* 环境检查 */}
          <Col span={12}>
            <Card size="small" title="环境检查">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <CodeOutlined />
                    <Text>Node.js</Text>
                  </Space>
                  <Space>
                    {getStatusTag(systemInfo.nodejs.status)}
                    <Text type="secondary">{systemInfo.nodejs.version}</Text>
                  </Space>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <DatabaseOutlined />
                    <Text>MySQL</Text>
                  </Space>
                  <Space>
                    {getStatusTag(systemInfo.mysql.status, systemInfo.mysql.connection)}
                    <Text type="secondary">{systemInfo.mysql.version}</Text>
                    <Button size="small" onClick={() => testConnection('mysql')}>
                      测试
                    </Button>
                  </Space>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <DockerOutlined />
                    <Text>Docker</Text>
                  </Space>
                  <Space>
                    {getStatusTag(systemInfo.docker.status, systemInfo.docker.running)}
                    <Text type="secondary">{systemInfo.docker.version}</Text>
                    <Button size="small" onClick={() => testConnection('docker')}>
                      测试
                    </Button>
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>

          {/* 系统信息 */}
          <Col span={12}>
            <Card size="small" title="系统信息">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>平台: </Text>
                  <Text>{systemInfo.system.platform} ({systemInfo.system.arch})</Text>
                </div>
                <div>
                  <Text strong>运行时间: </Text>
                  <Text>{formatUptime(systemInfo.system.uptime)}</Text>
                </div>
                <div>
                  <Text strong>内存使用: </Text>
                  <Progress
                    percent={Math.round((systemInfo.system.memory.used / systemInfo.system.memory.total) * 100)}
                    size="small"
                    format={() => `${formatBytes(systemInfo.system.memory.used)} / ${formatBytes(systemInfo.system.memory.total)}`}
                  />
                </div>
                <div>
                  <Text strong>磁盘使用: </Text>
                  <Progress
                    percent={Math.round((systemInfo.system.disk.used / systemInfo.system.disk.total) * 100)}
                    size="small"
                    format={() => `${formatBytes(systemInfo.system.disk.used)} / ${formatBytes(systemInfo.system.disk.total)}`}
                  />
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      ) : (
        <Spin size="large" />
      )}
    </Card>
  );

  // 基础设置组件
  const BasicSettingsCard = () => (
    <Card title="基础设置">
      {config && (
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={config}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="网站名称"
                name="site_name"
                rules={[{ required: true, message: '请输入网站名称' }]}
              >
                <Input placeholder="请输入网站名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="网站描述"
                name="site_description"
              >
                <Input placeholder="请输入网站描述" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>LOGO设置</Divider>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label="LOGO上传">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload
                    accept="image/*"
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />}>
                      选择LOGO文件
                    </Button>
                  </Upload>
                  
                  {config?.logo_url && (
                    <div style={{ marginTop: '16px' }}>
                      <Space>
                        <Image
                          src={config.logo_url}
                          alt="LOGO预览"
                          width={config.logo_width || 32}
                          height={config.logo_height || 32}
                          style={{ border: '1px solid #d9d9d9', borderRadius: '4px' }}
                        />
                        <Space direction="vertical">
                          <Button 
                            type="link" 
                            icon={<EyeOutlined />}
                            onClick={handleLogoPreview}
                          >
                            预览
                          </Button>
                          <Button 
                            type="link" 
                            danger
                            icon={<DeleteOutlined />}
                            onClick={handleLogoDelete}
                          >
                            删除
                          </Button>
                        </Space>
                      </Space>
                    </div>
                  )}
                  
                  <Text type="secondary">
                    支持 JPG、PNG、GIF 格式，文件大小不超过 5MB
                  </Text>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="LOGO宽度 (px)"
                name="logo_width"
                tooltip="LOGO的显示宽度，单位：像素"
              >
                <InputNumber 
                  min={16} 
                  max={200} 
                  style={{ width: '100%' }} 
                  placeholder="32"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="LOGO高度 (px)"
                name="logo_height"
                tooltip="LOGO的显示高度，单位：像素"
              >
                <InputNumber 
                  min={16} 
                  max={200} 
                  style={{ width: '100%' }} 
                  placeholder="32"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="维护模式"
                name="maintenance_mode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="调试模式"
                name="debug_mode"
                valuePropName="checked"
                tooltip="启用调试模式将在前端显示详细的调试信息"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="淘宝收货码验证"
                name="taobao_verification_enabled"
                valuePropName="checked"
                tooltip="启用后，激活码创建的服务器需要验证淘宝收货码才能使用完整功能"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="每用户最大服务器数"
                name="max_servers_per_user"
                rules={[{ required: true, message: '请输入最大服务器数' }]}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );

  // 备份设置组件
  const BackupSettingsCard = () => (
    <Card title="备份设置">
      {config && (
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={config}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="自动备份"
                name="auto_backup"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="备份间隔(小时)"
                name="backup_interval"
              >
                <InputNumber min={1} max={168} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="备份设置说明"
            description={
              <div>
                <p>• 自动备份功能将定期备份数据库和重要配置文件</p>
                <p>• 备份文件将保存在服务器的备份目录中</p>
                <p>• 建议设置合理的备份间隔，避免频繁备份影响性能</p>
                <p>• 定期检查备份文件，确保备份功能正常工作</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );

  // 邮件设置组件
  const EmailSettingsCard = () => (
    <Card title="邮件设置">
      {config && (
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={config}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="启用邮件通知"
                name="email_enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="SMTP主机"
                name="smtp_host"
              >
                <Input placeholder="smtp.example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="SMTP端口"
                name="smtp_port"
              >
                <InputNumber min={1} max={65535} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="SMTP用户名"
                name="smtp_user"
              >
                <Input placeholder="username@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="SMTP密码"
            name="smtp_password"
          >
            <Input.Password placeholder="请输入SMTP密码" />
          </Form.Item>

          <Form.Item
            label="邮件发送者名称"
            name="email_sender_name"
            tooltip="自定义邮件发送者的显示名称，例如：游戏服务器管理面板。如果留空，将只显示邮箱地址。"
          >
            <Input 
              placeholder="例如：游戏服务器管理面板（可选）" 
              maxLength={50}
            />
          </Form.Item>

          <Form.Item
            label="面板TODO管理员通知邮箱"
            name="panel_todo_admin_email"
            tooltip="当用户提交新的TODO反馈时，将发送通知邮件到此邮箱。如果未配置，将使用SMTP用户名作为通知邮箱。"
          >
            <Input 
              type="email"
              placeholder="admin@example.com（可选，默认使用SMTP用户名）" 
            />
          </Form.Item>

          <Alert
            message="邮件设置说明"
            description={
              <div>
                <p>• 邮件通知功能用于发送服务器状态、用户注册等重要信息</p>
                <p>• 请确保SMTP服务器配置正确，避免邮件发送失败</p>
                <p>• 建议使用企业邮箱或专业的邮件服务提供商</p>
                <p>• 面板TODO管理员通知邮箱：当用户提交新的TODO反馈时，将发送通知到此邮箱</p>
                <p>• 定期检查邮件发送日志，确保邮件功能正常</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );

  // 支付设置组件
  const PaymentSettingsCard = () => (
    <Card title="支付设置">
      {config && (
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={config}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label="启用彩虹易支付"
                name="rainbow_pay_enabled"
                valuePropName="checked"
                tooltip="启用后，用户可以使用彩虹易支付进行充值和购买服务器"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider>支付方式设置</Divider>

          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item
                label="微信支付"
                name="rainbow_pay_wechat_enabled"
                valuePropName="checked"
                tooltip="启用微信支付"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="支付宝"
                name="rainbow_pay_alipay_enabled"
                valuePropName="checked"
                tooltip="启用支付宝支付"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="QQ钱包"
                name="rainbow_pay_qq_enabled"
                valuePropName="checked"
                tooltip="启用QQ钱包支付"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="商户ID (PID)"
                name="rainbow_pay_pid"
                tooltip="彩虹易支付商户ID"
              >
                <Input placeholder="请输入商户ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="商户密钥 (KEY)"
                name="rainbow_pay_key"
                tooltip="彩虹易支付商户密钥"
              >
                <Input.Password placeholder="请输入商户密钥" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="API接口地址"
            name="rainbow_pay_api_url"
            tooltip="彩虹易支付API接口地址"
          >
            <Input placeholder="https://pay.v8jisu.cn/submit.php" />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="异步通知地址"
                name="rainbow_pay_notify_url"
                tooltip="支付成功后异步通知的URL地址"
              >
                <Input placeholder="https://yourdomain.com/api/payment/rainbow/notify" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="同步返回地址"
                name="rainbow_pay_return_url"
                tooltip="支付完成后用户返回的URL地址"
              >
                <Input placeholder="https://yourdomain.com/payment/notify" />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="彩虹易支付配置说明"
            description={
              <div>
                <p>1. 请先在彩虹易支付平台注册商户账号</p>
                <p>2. 获取商户ID (PID) 和商户密钥 (KEY)</p>
                <p>3. 配置正确的异步通知地址和同步返回地址</p>
                <p>4. 确保服务器可以访问彩虹易支付API接口</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );

  // API设置组件
  const ApiSettingsCard = () => (
    <Card title="API设置">
      {config && (
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={config}
        >
          <Form.Item
            label="Steam Web API密钥"
            name="steam_api_key"
            tooltip="用于获取Steam Workshop MOD信息的API密钥，可在Steam开发者页面申请"
          >
            <Input.Password 
              placeholder="请输入Steam Web API密钥" 
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Alert
            message="Steam Web API密钥获取方法"
            description={
              <div>
                <p>1. 访问 <a href="https://steamcommunity.com/dev/apikey" target="_blank" rel="noopener noreferrer">Steam Web API Key页面</a></p>
                <p>2. 使用您的Steam账号登录</p>
                <p>3. 输入域名（如：localhost 或您的域名）</p>
                <p>4. 点击"Register"获取API密钥</p>
                <p>5. 将获取的密钥粘贴到上方输入框中</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <SettingOutlined style={{ marginRight: '8px' }} />
        系统设置
      </Title>

      <Tabs
        defaultActiveKey="status"
        type="card"
        items={[
          {
            key: 'status',
            label: (
              <span>
                <MonitorOutlined />
                系统状态
              </span>
            ),
            children: <SystemStatusCard />
          },
          {
            key: 'basic',
            label: (
              <span>
                <GlobalOutlined />
                基础设置
              </span>
            ),
            children: <BasicSettingsCard />
          },
          {
            key: 'backup',
            label: (
              <span>
                <CloudUploadOutlined />
                备份设置
              </span>
            ),
            children: <BackupSettingsCard />
          },
          {
            key: 'email',
            label: (
              <span>
                <MailOutlined />
                邮件设置
              </span>
            ),
            children: <EmailSettingsCard />
          },
          {
            key: 'payment',
            label: (
              <span>
                <CreditCardOutlined />
                支付设置
              </span>
            ),
            children: <PaymentSettingsCard />
          },
          {
            key: 'api',
            label: (
              <span>
                <ApiOutlined />
                API设置
              </span>
            ),
            children: <ApiSettingsCard />
          }
        ]}
      />

      {/* 系统警告 */}
      <Alert
        message="系统提示"
        description={
          <div>
            <Paragraph>
              • 确保所有环境组件正常运行，否则可能影响服务器创建和管理功能
            </Paragraph>
            <Paragraph>
              • 定期检查系统资源使用情况，避免磁盘空间不足
            </Paragraph>
            <Paragraph>
              • 建议启用自动备份功能，定期备份重要数据
            </Paragraph>
            <Paragraph>
              • 维护模式下用户将无法访问系统，请谨慎使用
            </Paragraph>
          </div>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginTop: 16 }}
      />

      {/* LOGO预览模态框 */}
      <Modal
        title="LOGO预览"
        open={logoModalVisible}
        onCancel={() => setLogoModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLogoModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {logoPreview && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={logoPreview}
              alt="LOGO预览"
              style={{ maxWidth: '100%', maxHeight: '400px' }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                当前尺寸: {config?.logo_width || 32} × {config?.logo_height || 32} 像素
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 图片剪裁模态框 */}
      <ImageCropModal
        visible={cropModalVisible}
        imageUrl={cropImageUrl}
        onCancel={handleCropCancel}
        onCrop={handleCropComplete}
        aspectRatio={1} // 正方形裁剪
      />
    </div>
  );
};

export default Settings;
